// Cross-module deep import tolerated (ADR-6) — vendor exposes no repository on
// its public API; only the presenter/entity/mocks are public.
import { vendorRepository } from '@webapp/modules/vendor/data-access/repositories/VendorRepository'
import { type VendorPaymentMethodConstructor } from '@webapp/modules/vendor/entities/VendorPaymentMethod'
import { approvalChainRepository } from '@webapp/platform/approval-routing'
import { type BillApprovalChainOptionConstructor } from '@webapp/platform/approval-routing/entities/BillApprovalChainOption'

// Use case owns its own narrow input types — no UI/form/DS types.
export interface GetCreditCardBillFormDataParams {
  vendorId: number
  /** Card currency — payment-method options are filtered to it (BHV-045) */
  cardCurrencyId: number
}

export interface GetCreditCardBillFormDataResult {
  paymentMethods: VendorPaymentMethodConstructor[]
  approvalChains: BillApprovalChainOptionConstructor[]
  /** metadata.skip_bill_approval — hides the Approval Group/Approver row (BHV-046) */
  skipApproval: boolean
}

// Bundles the bill form's supporting data (Feature-Set-Composition hydration
// pattern): vendor payment methods (filtered to the card currency) + bill
// approval chains, fetched in parallel.
export const getCreditCardBillFormData = async (
  params: GetCreditCardBillFormDataParams
): Promise<GetCreditCardBillFormDataResult> => {
  const [paymentMethods, approvalChainsResult] = await Promise.all([
    vendorRepository.getVendorPaymentMethods(params.vendorId),
    approvalChainRepository.listBillApprovalChains(),
  ])

  return {
    paymentMethods: paymentMethods.filter(
      (method) => method.currencyId === params.cardCurrencyId
    ),
    approvalChains: approvalChainsResult.chains,
    skipApproval: approvalChainsResult.skipApproval,
  }
}
